// Kaun Banega Cables-pati — game engine constants & helpers.
//
// Prize ladder (16 questions). Q5 and Q10 are safe milestones — if you answer wrong below
// them you fall to the previous safe stage; if you walk away you take whatever the last
// LOCKED question was worth.

export const LADDER = [
    10,        // Q1
    20,        // Q2
    30,        // Q3
    50,        // Q4
    100,       // Q5  ★ Safe milestone 1
    200,       // Q6
    400,       // Q7
    800,       // Q8
    1600,      // Q9
    3200,      // Q10 ★ Safe milestone 2
    6400,      // Q11
    12500,     // Q12
    25000,     // Q13
    50000,     // Q14
    100000,    // Q15
    700000,    // Q16 ★ Jackpot
];

// 0-indexed safe stages (after answering Q5 = index 4 correctly you cannot fall below 100 pts)
export const SAFE_INDICES = [4, 9];
export const JACKPOT_INDEX = LADDER.length - 1;

export const QUESTION_SECONDS = 45; // 45s timer per question. Pause during lifeline modal.

// Speed-bonus tiers: divide the timer into 4 equal quarters. Faster answer = bigger multiplier.
//   Quarter 1 (top 25% of time remaining) → 4x base points
//   Quarter 2 → 3x
//   Quarter 3 → 2x
//   Quarter 4 (last 25%) → 1x
// Final points awarded for a question = LADDER[idx] * speedMultiplier(secondsLeft).
export const SPEED_TIERS = [
    { from: 0.75, mult: 4, label: 'BLAZING', color: '#34d399' },   // top quarter
    { from: 0.50, mult: 3, label: 'FAST',    color: '#fde047' },
    { from: 0.25, mult: 2, label: 'STEADY',  color: '#fb923c' },
    { from: 0.00, mult: 1, label: 'CLUTCH',  color: '#ef4444' },
];

export function speedMultiplier(secondsLeft, total = QUESTION_SECONDS) {
    const pct = Math.max(0, secondsLeft) / total;
    for (const tier of SPEED_TIERS) {
        if (pct > tier.from) return tier.mult;
    }
    return 1;
}

export function speedTier(secondsLeft, total = QUESTION_SECONDS) {
    const pct = Math.max(0, secondsLeft) / total;
    for (const tier of SPEED_TIERS) {
        if (pct > tier.from) return tier;
    }
    return SPEED_TIERS[SPEED_TIERS.length - 1];
}

export const TOPIC_PRESETS = [
    'Propulse FR/FRLS house wiring & safety',
    'XLPE LT & HT power cables',
    'IS 694 / IS 7098 / IS 1554 standards',
    'Electrical site safety, PPE & LOTO',
    'Cable drum handling & counterfeit detection',
    'Channel partner billing & inventory ops',
    'Electrician troubleshooting basics',
];

export const LIFELINES = ['fifty_fifty', 'audience_poll', 'phone_a_friend', 'expert_advice'];

export const LIFELINE_META = {
    fifty_fifty: { name: '50:50', short: '50', detail: 'Remove 2 wrong options' },
    audience_poll: { name: 'Audience Poll', short: 'AP', detail: 'See audience % per option' },
    phone_a_friend: { name: 'Phone a Friend', short: 'PF', detail: 'Get a friend\'s hint' },
    expert_advice: { name: 'Expert Advice', short: 'EX', detail: 'Ask a senior engineer' },
};

export const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

export function formatPoints(n) {
    if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} L`;
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
    return `${n}`;
}

// Compute the points the user takes home when they answer Q[idx] wrong.
// idx is 0-indexed. SAFE_INDICES define floors.
export function fallbackPointsOnWrong(idx) {
    // Floor of last safe milestone strictly BELOW idx (not the question being answered)
    let floor = 0;
    for (const safe of SAFE_INDICES) {
        if (safe < idx) floor = LADDER[safe];
    }
    return floor;
}

// Walk-away points = whatever the last cleared question was worth (LADDER[idx-1]) or 0.
export function walkAwayPoints(idx) {
    if (idx <= 0) return 0;
    return LADDER[idx - 1];
}

// For 50:50 — pick 2 wrong indices to keep hidden (deterministic per question).
export function pickFiftyFiftyHidden(correctIndex) {
    const wrong = [0, 1, 2, 3].filter((i) => i !== correctIndex);
    // Shuffle deterministically by correctIndex
    const sorted = [...wrong].sort((a, b) => ((a + correctIndex) % 3) - ((b + correctIndex) % 3));
    return [sorted[0], sorted[1]];
}

// Fallback bank for offline / AI-failure path — these mirror the seeded `config.fallback_questions`.
// Used only as a final safety net. UI primarily uses AI bank.
export const FALLBACK_BANK = [
    { question: 'What does FRLS stand for in cables?', options: ['Fire-Resistant Low Surge', 'Flame Retardant Low Smoke', 'Fully Recyclable Lead-free', 'Flexible Rated Long Service'], correct_index: 1, explanation: 'FRLS = Flame Retardant Low Smoke.', friend_hint: "Bhai, FRLS is Flame Retardant Low Smoke. We use those wires in all hospital projects.", expert_insight: 'Flame Retardant Low Smoke — option B.', audience_poll: [12, 71, 9, 8] },
    { question: 'What colour is the earth wire as per IS 11353?', options: ['Red', 'Black', 'Green / Green-Yellow', 'Blue'], correct_index: 2, explanation: 'Green or green-yellow striped is the universal earth identifier.', friend_hint: "Earth wire is always green or green-yellow.", expert_insight: 'IS 11353 mandates Green/Yellow striped for earth — option C.', audience_poll: [4, 6, 84, 6] },
    { question: 'Which Indian Standard governs PVC insulated domestic wires up to 1100V?', options: ['IS 1554', 'IS 694', 'IS 7098', 'IS 9968'], correct_index: 1, explanation: 'IS 694 covers PVC insulated cables up to 1100V.', friend_hint: "Pretty sure it's IS 694 — that's the house wiring standard.", expert_insight: 'IS 694:2010 — option B.', audience_poll: [8, 72, 14, 6] },
    { question: 'Minimum copper conductor size for a 16A AC point?', options: ['0.5 sq.mm', '1.0 sq.mm', '2.5 sq.mm', '4.0 sq.mm'], correct_index: 3, explanation: '4 sq.mm copper for 16A circuits.', friend_hint: "For 16 amp AC point — 4 square mm.", expert_insight: '4 sq.mm copper — option D.', audience_poll: [3, 7, 32, 58] },
    { question: 'XLPE\'s key advantage over PVC insulation?', options: ['Lower cost', 'Higher operating temperature (90°C)', 'Easier to colour', 'Better flexibility'], correct_index: 1, explanation: 'XLPE handles 90°C continuous vs PVC 70°C.', friend_hint: "Temperature rating — XLPE goes to 90 continuous, PVC only 70.", expert_insight: 'XLPE tolerates 90°C continuous — option B.', audience_poll: [8, 64, 9, 19] },
    { question: 'Mandatory PPE for live LV switchgear?', options: ['Sunglasses + cotton gloves', 'Class-0 insulated gloves + arc-rated face shield', 'Rubber slippers + helmet', 'Plastic apron + goggles'], correct_index: 1, explanation: 'Class-0 insulated gloves + arc-rated face shield.', friend_hint: "Class-0 gloves with arc shield. Anything else fails audit.", expert_insight: 'IS 4770 mandates option B.', audience_poll: [3, 78, 8, 11] },
    { question: 'Best cable drum storage practice?', options: ['Flat on side', 'Upright on flanges, covered dry area', 'Vertical outdoors, no cover', 'On wet ground'], correct_index: 1, explanation: 'Upright on flanges in dry covered storage.', friend_hint: "Upright on flanges, under cover.", expert_insight: 'IS 10810 — option B.', audience_poll: [5, 79, 10, 6] },
    { question: 'XLPE LT power cables are rated up to?', options: ['440 V', '1.1 kV', '11 kV', '33 kV'], correct_index: 1, explanation: 'LT XLPE is rated up to 1.1 kV.', friend_hint: "LT means 1.1 kV.", expert_insight: 'LT XLPE per IS 7098 Part 1 — option B.', audience_poll: [11, 58, 22, 9] },
    { question: 'Premium FRLS house-wire conductor spec?', options: ['Aluminium', 'Brass alloy', '99.97% electrolytic copper', 'Galvanised steel'], correct_index: 2, explanation: '99.97% electrolytic copper.', friend_hint: "Electrolytic copper — 99.97% pure.", expert_insight: 'ETP grade copper — option C.', audience_poll: [9, 4, 78, 9] },
    { question: 'Min insulation resistance for new house wiring?', options: ['0.1 MΩ', '0.5 MΩ', '1 MΩ (500V megger)', '50 MΩ'], correct_index: 2, explanation: '≥1 MΩ at 500V.', friend_hint: "Megger test — minimum 1 megohm.", expert_insight: '≥1 MΩ at 500V — option C.', audience_poll: [6, 22, 60, 12] },
    { question: 'Fastest counterfeit (CCA) detection?', options: ['Visual only', 'Smell test', 'Bend + cut cross-section + density', 'Tape test'], correct_index: 2, explanation: 'Cross-section shows silver core in CCA.', friend_hint: "Bend and cut — pure copper stays red inside.", expert_insight: 'Cross-section + density — option C.', audience_poll: [12, 4, 66, 18] },
    { question: 'First step of LOTO procedure?', options: ['Test for zero energy', 'Apply lock and tag', 'Notify personnel + identify energy sources', 'Begin repair'], correct_index: 2, explanation: 'LOTO Step 1: Notify and Identify.', friend_hint: "First step is always notify and identify.", expert_insight: 'OSHA LOTO sequence — option C.', audience_poll: [18, 22, 48, 12] },
    { question: '3-core 95 sq.mm XLPE LT direct-buried ampacity?', options: ['~120 A', '~180 A', '~245 A', '~360 A'], correct_index: 2, explanation: '~245 A at standard conditions.', friend_hint: "Around 240-250 amps — but check the derating chart.", expert_insight: 'IS 7098-1 Annex B: ~245 A — option C.', audience_poll: [14, 26, 41, 19] },
    { question: 'Short-circuit thermal limit of XLPE?', options: ['150°C', '200°C', '250°C', '350°C'], correct_index: 2, explanation: '250°C for up to 5 seconds.', friend_hint: "250 — definitely higher than PVC.", expert_insight: 'IS 7098 short-circuit limit — option C.', audience_poll: [11, 18, 53, 18] },
    { question: 'Magnet shows zero pull on a conductor sample. Of these, which is acceptable?', options: ['Steel cored', 'Copper-clad aluminium', 'Pure copper', 'Brass'], correct_index: 2, explanation: 'Both Cu and CCA are non-magnetic — pair with cross-section test. Pure copper is the only acceptable answer here.', friend_hint: "Magnet test doesn't distinguish Cu vs CCA — but of those four, pure copper is acceptable.", expert_insight: 'Pair magnet test with density/cross-section — option C is the only acceptable conductor here.', audience_poll: [13, 28, 42, 17] },
    { question: 'IS 1554 Part 1 covers PVC cables rated up to?', options: ['650/1100 V', '1.9/3.3 kV', '3.8/6.6 kV', '11 kV'], correct_index: 0, explanation: 'IS 1554 Part 1 → up to 1100V.', friend_hint: "Part 1 is the LT — 650/1100 V.", expert_insight: 'IS 1554 Part 1: up to 1100V — option A.', audience_poll: [40, 28, 19, 13] },
];
